import { useAuth } from "../lib/auth";
import { useNavigate, Navigate } from "react-router-dom";
import { useState, useEffect, useRef } from "react";
import axios from "axios";
import {
  Sparkles, Clock, FileText, Zap, ArrowRight, TrendingUp,
  Target, Briefcase, FolderOpen, Mail, AlertTriangle, Chrome, Download
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function DashboardHome() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [recentHistory, setRecentHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [extVersion, setExtVersion] = useState("");

  const plan = user?.subscription?.plan || "trial";
  const isNoPlan = plan === "none";
  const isUnlimited = plan === "unlimited";
  const u = user?.usage || {};
  const optLimit = u.opt_limit ?? u.scans_limit ?? 0;
  const optUsed = u.opt_used ?? u.scans_used ?? 0;
  const optAddon = u.opt_addon ?? u.addon_scans ?? 0;
  const scanLimit = u.scan_limit ?? u.scans_limit ?? 0;
  const scanUsed = u.scan_used ?? 0;
  const scanAddon = u.scan_addon ?? 0;
  const optRemaining = isUnlimited ? null : Math.max(0, optLimit - optUsed + optAddon);
  const scanRemaining = isUnlimited ? null : Math.max(0, scanLimit - scanUsed + scanAddon);
  const optPct = isUnlimited ? 0 : (optLimit > 0 ? Math.min(100, Math.round((optUsed / optLimit) * 100)) : 0);
  const scanPct = isUnlimited ? 0 : (scanLimit > 0 ? Math.min(100, Math.round((scanUsed / scanLimit) * 100)) : 0);
  const trialSkipped = user?.trial?.skipped === true;
  // Low-credit upsell: fire when a wallet drops to ~10% or less (but the user still has a plan).
  const optLow = !isUnlimited && optLimit > 0 && optRemaining <= Math.max(1, Math.ceil(optLimit * 0.1));
  const scanLow = !isUnlimited && scanLimit > 0 && scanRemaining <= Math.max(1, Math.ceil(scanLimit * 0.1));
  const showLowCreditNudge = !trialSkipped && !isNoPlan && (optLow || scanLow);
  const planLabel = isNoPlan ? "No Plan" : plan === "trial" ? "Free Trial" : plan === "base" ? "Base Plan" : "Unlimited";

  const trialExpires = user?.trial?.expires_at;
  const daysLeft = trialExpires
    ? Math.max(0, Math.ceil((new Date(trialExpires) - new Date()) / (1000 * 60 * 60 * 24)))
    : 0;

  const subStatus = user?.subscription?.status || "active";

  useEffect(() => {
    const fetchRecent = async () => {
      try {
        const res = await axios.get(`${API}/history`);
        setRecentHistory(res.data.slice(0, 5));
      } catch { /* silent */ }
      finally { setLoading(false); }
    };
    fetchRecent();
    axios.get(`${API}/extension/version`).then((r) => setExtVersion(r.data?.version || "")).catch(() => {});
  }, []);

  const getScoreColor = (val) => {
    if (val >= 75) return "var(--success)";
    if (val >= 50) return "var(--warning)";
    return "var(--error)";
  };

  const formatDate = (iso) => {
    try {
      return new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric" });
    } catch { return ""; }
  };

  const avgScore = recentHistory.length > 0
    ? Math.round(recentHistory.reduce((sum, h) => sum + (h.ats_score?.overall_score || 0), 0) / recentHistory.length)
    : 0;

  const [scoreTrends, setScoreTrends] = useState([]);
  const chartRef = useRef(null);

  useEffect(() => {
    const fetchTrends = async () => {
      try {
        const res = await axios.get(`${API}/score-trends`);
        setScoreTrends(res.data.slice(-12)); // last 12 optimizations
      } catch { /* silent */ }
    };
    fetchTrends();
  }, []);

  // Draw score trend chart on canvas
  useEffect(() => {
    const canvas = chartRef.current;
    if (!canvas || scoreTrends.length < 2) return;
    const ctx = canvas.getContext("2d");
    const dpr = window.devicePixelRatio || 1;
    const rect = canvas.getBoundingClientRect();
    canvas.width = rect.width * dpr;
    canvas.height = rect.height * dpr;
    ctx.scale(dpr, dpr);
    const w = rect.width;
    const h = rect.height;
    const pad = { top: 20, right: 16, bottom: 32, left: 36 };
    const chartW = w - pad.left - pad.right;
    const chartH = h - pad.top - pad.bottom;

    ctx.clearRect(0, 0, w, h);

    // Y axis: 0-100
    ctx.font = "10px Inter, sans-serif";
    ctx.fillStyle = "#64748b";
    ctx.textAlign = "right";
    for (let v = 0; v <= 100; v += 25) {
      const y = pad.top + chartH - (v / 100) * chartH;
      ctx.fillText(v.toString(), pad.left - 6, y + 3);
      ctx.strokeStyle = "rgba(148,163,184,0.08)";
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.moveTo(pad.left, y);
      ctx.lineTo(w - pad.right, y);
      ctx.stroke();
    }

    // X axis labels (dates)
    ctx.textAlign = "center";
    ctx.fillStyle = "#64748b";
    const step = chartW / (scoreTrends.length - 1);
    scoreTrends.forEach((d, i) => {
      const x = pad.left + i * step;
      if (i % Math.ceil(scoreTrends.length / 6) === 0 || i === scoreTrends.length - 1) {
        try {
          const dt = new Date(d.date);
          ctx.fillText(`${dt.getMonth() + 1}/${dt.getDate()}`, x, h - 8);
        } catch { /* skip */ }
      }
    });

    // Draw gradient fill
    const gradient = ctx.createLinearGradient(0, pad.top, 0, pad.top + chartH);
    gradient.addColorStop(0, "rgba(99,102,241,0.2)");
    gradient.addColorStop(1, "rgba(99,102,241,0)");

    ctx.beginPath();
    scoreTrends.forEach((d, i) => {
      const x = pad.left + i * step;
      const y = pad.top + chartH - (d.overall / 100) * chartH;
      if (i === 0) ctx.moveTo(x, y);
      else ctx.lineTo(x, y);
    });
    ctx.lineTo(pad.left + (scoreTrends.length - 1) * step, pad.top + chartH);
    ctx.lineTo(pad.left, pad.top + chartH);
    ctx.closePath();
    ctx.fillStyle = gradient;
    ctx.fill();

    // Draw line
    ctx.beginPath();
    scoreTrends.forEach((d, i) => {
      const x = pad.left + i * step;
      const y = pad.top + chartH - (d.overall / 100) * chartH;
      if (i === 0) ctx.moveTo(x, y);
      else ctx.lineTo(x, y);
    });
    ctx.strokeStyle = "#6366f1";
    ctx.lineWidth = 2;
    ctx.lineJoin = "round";
    ctx.stroke();

    // Draw dots
    scoreTrends.forEach((d, i) => {
      const x = pad.left + i * step;
      const y = pad.top + chartH - (d.overall / 100) * chartH;
      ctx.beginPath();
      ctx.arc(x, y, 3.5, 0, Math.PI * 2);
      ctx.fillStyle = d.overall >= 75 ? "#10b981" : d.overall >= 50 ? "#f59e0b" : "#ef4444";
      ctx.fill();
      ctx.strokeStyle = "var(--bg-deep, #020617)";
      ctx.lineWidth = 1.5;
      ctx.stroke();
    });
  }, [scoreTrends]);

  if (user?.workspace_mode === "agency" && user?.primary_agency_id) {
    return <Navigate to={`/app/agencies/${user.primary_agency_id}`} replace />;
  }

  return (
    <div className="dash-home" data-testid="dashboard-home">
      {/* Welcome Header */}
      <div className="dash-welcome">
        <div>
          <h1 data-testid="dashboard-greeting">
            Welcome back, {user?.name?.split(" ")[0] || "there"}
          </h1>
          <p className="dash-welcome-sub">
            {trialSkipped
              ? "Free trial not available for this account"
              : plan === "trial"
              ? `${daysLeft} days left on your free trial`
              : isNoPlan
              ? "Subscribe to start optimizing resumes"
              : `You're on the ${planLabel}`}
            {subStatus === "active" && plan !== "trial" && !isNoPlan && (
              <span style={{ color: "var(--success)", marginLeft: "0.5rem", fontSize: "0.8rem", fontWeight: 600 }}>Active</span>
            )}
          </p>
        </div>
        <button className="dash-cta-btn" onClick={() => navigate("/app/optimize")} data-testid="dashboard-optimize-btn">
          <Sparkles size={16} /> New Optimization
        </button>
      </div>

      {/* Trial Skipped Banner */}
      {trialSkipped && (
        <div className="dash-trial-skipped-banner" data-testid="trial-skipped-banner">
          <div className="dash-trial-skipped-content">
            <AlertTriangle size={18} />
            <div>
              <strong>Free trial already used</strong>
              <p>Your free trial was used on another account from this device. Please log into your previous account or subscribe to continue.</p>
            </div>
          </div>
          <button className="dash-trial-skipped-cta" onClick={() => navigate("/app/pricing")} data-testid="trial-skipped-upgrade-btn">
            View Plans
          </button>
        </div>
      )}

      {/* Low-credit upsell nudge */}
      {showLowCreditNudge && (
        <div className="dash-lowcredit-banner" data-testid="low-credit-nudge">
          <div className="dash-lowcredit-content">
            <Zap size={18} />
            <div>
              <strong data-testid="low-credit-title">
                {optLow && scanLow
                  ? "You're running low on credits"
                  : optLow
                  ? `Only ${optRemaining} optimization${optRemaining === 1 ? "" : "s"} left`
                  : `Only ${scanRemaining} scan${scanRemaining === 1 ? "" : "s"} left`}
              </strong>
              <p data-testid="low-credit-subtitle">
                {optLow && scanLow
                  ? `${optRemaining} optimizations and ${scanRemaining} scans remaining. Top up so your job search never pauses.`
                  : optLow
                  ? "Grab an optimization pack or upgrade — takes seconds, keeps you tailoring."
                  : "Grab a scan pack or upgrade — keep checking your match score without interruptions."}
              </p>
            </div>
          </div>
          <button className="dash-lowcredit-cta" onClick={() => navigate("/app/pricing")} data-testid="low-credit-topup-btn">
            Top up credits
          </button>
        </div>
      )}

      {/* Stats Grid */}
      <div className="dash-stats-grid" data-testid="dashboard-stats">
        <div className="dash-stat-card dash-stat-primary" data-testid="stat-optimizations-remaining">
          <div className="dash-stat-icon"><Sparkles size={20} /></div>
          <div className="dash-stat-content">
            <div className="dash-stat-label">Optimizations Remaining</div>
            <div className="dash-stat-value" data-testid="opt-remaining-value">
              {isUnlimited ? "Unlimited" : optRemaining}
            </div>
            {!isUnlimited && (
              <div className="dash-stat-bar-wrap">
                <div className="dash-stat-bar">
                  <div className="dash-stat-bar-fill" style={{ width: `${100 - optPct}%` }} />
                </div>
                <span className="dash-stat-bar-label">{optUsed} of {optLimit} used</span>
              </div>
            )}
            {isUnlimited && (
              <span className="dash-stat-bar-label" style={{ color: "var(--success)" }}>No limits on your plan</span>
            )}
          </div>
        </div>

        <div className="dash-stat-card" data-testid="stat-scans">
          <div className="dash-stat-icon"><Target size={20} /></div>
          <div className="dash-stat-content">
            <div className="dash-stat-label">Scans Remaining</div>
            <div className="dash-stat-value" data-testid="scans-remaining-value">
              {isUnlimited ? "Unlimited" : scanRemaining}
            </div>
            {!isUnlimited && (
              <div className="dash-stat-bar-wrap">
                <div className="dash-stat-bar">
                  <div className="dash-stat-bar-fill" style={{ width: `${100 - scanPct}%` }} />
                </div>
                <span className="dash-stat-bar-label">{scanUsed} of {scanLimit} used</span>
              </div>
            )}
            {isUnlimited && (
              <span className="dash-stat-bar-label" style={{ color: "var(--success)" }}>No scan limits on your plan</span>
            )}
          </div>
        </div>

        <div className="dash-stat-card" data-testid="stat-avg-score">
          <div className="dash-stat-icon"><TrendingUp size={20} /></div>
          <div className="dash-stat-content">
            <div className="dash-stat-label">Avg ATS Score</div>
            <div className="dash-stat-value" style={{ color: avgScore > 0 ? getScoreColor(avgScore) : undefined }}>
              {avgScore > 0 ? `${avgScore}%` : "--"}
            </div>
          </div>
        </div>

        <div className="dash-stat-card" data-testid="stat-plan">
          <div className="dash-stat-icon"><Zap size={20} /></div>
          <div className="dash-stat-content">
            <div className="dash-stat-label">Current Plan</div>
            <div className="dash-stat-value dash-stat-plan">{planLabel}</div>
            {plan === "trial" && (
              <span className="dash-stat-expires">{daysLeft}d remaining</span>
            )}
          </div>
        </div>
      </div>

      {/* Score Trend Chart */}
      {scoreTrends.length >= 2 && (
        <div className="dash-section" data-testid="score-trends">
          <div className="dash-section-header">
            <h2><TrendingUp size={18} /> ATS Score Trends</h2>
            <span className="dash-trend-summary">{scoreTrends.length} optimizations tracked</span>
          </div>
          <div className="dash-chart-container" data-testid="score-trends-chart">
            <canvas ref={chartRef} className="dash-chart-canvas" />
          </div>
        </div>
      )}

      {/* Quick Actions */}
      <div className="dash-section">
        <h2>Quick Actions</h2>
        <div className="dash-actions-grid" data-testid="quick-actions">
          <button className="dash-action-card" onClick={() => navigate("/app/optimize")} data-testid="action-optimize">
            <Sparkles size={22} />
            <span>Optimize Resume</span>
          </button>
          <button className="dash-action-card" onClick={() => navigate("/app/cover-letter")} data-testid="action-cover-letter">
            <Mail size={22} />
            <span>Generate Cover Letter</span>
          </button>
          <button className="dash-action-card" onClick={() => navigate("/app/tracker")} data-testid="action-tracker">
            <Briefcase size={22} />
            <span>Job Tracker</span>
          </button>
          <button className="dash-action-card" onClick={() => navigate("/app/resumes")} data-testid="action-resumes">
            <FolderOpen size={22} />
            <span>Resume Manager</span>
          </button>
          <button className="dash-action-card" onClick={() => navigate("/app/history")} data-testid="action-history">
            <Clock size={22} />
            <span>Scan History</span>
          </button>
        </div>
      </div>

      {/* Recent Optimizations */}
      <div className="dash-section" data-testid="dashboard-recent">
        <div className="dash-section-header">
          <h2>Recent Optimizations</h2>
          {recentHistory.length > 0 && (
            <button className="dash-link-btn" onClick={() => navigate("/app/history")} data-testid="view-all-history-btn">
              View all <ArrowRight size={14} />
            </button>
          )}
        </div>

        {loading ? (
          <div className="dash-loading"><div className="processing-spinner" /></div>
        ) : recentHistory.length === 0 ? (
          <div className="dash-empty-card" data-testid="dashboard-empty">
            <div className="dash-empty-icon"><FileText size={28} /></div>
            <h3>No optimizations yet</h3>
            <p>Upload your resume and a job description to get started.</p>
            <button className="dash-cta-btn" onClick={() => navigate("/app/optimize")} data-testid="dashboard-empty-optimize-btn">
              <Sparkles size={16} /> Start Optimizing
            </button>
          </div>
        ) : (
          <div className="dash-recent-list">
            {recentHistory.map((item) => (
              <div key={item.id} className="dash-recent-item" onClick={() => navigate("/app/history")} data-testid={`recent-item-${item.id}`}>
                <div className="dash-recent-left">
                  <div className="dash-recent-icon"><FileText size={16} /></div>
                  <div>
                    <div className="dash-recent-title">{item.job_title || "Untitled Optimization"}</div>
                    <div className="dash-recent-meta">
                      {item.company_name && <span>{item.company_name}</span>}
                      <span>{item.original_filename}</span>
                      <span>{formatDate(item.created_at)}</span>
                    </div>
                  </div>
                </div>
                <div className="dash-recent-score" style={{ color: getScoreColor(item.ats_score?.overall_score || 0) }} data-testid={`recent-score-${item.id}`}>
                  {item.ats_score?.overall_score || 0}%
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Chrome Extension Card */}
      <div className="dash-ext-card" data-testid="dashboard-extension-card">
        <div className="dash-ext-left">
          <Chrome size={22} className="dash-ext-chrome-icon" />
          <div>
            <h3 className="dash-ext-title">Chrome Extension{extVersion && <span data-testid="ext-version-badge" style={{ fontSize: "0.7rem", fontWeight: 700, opacity: 0.65, marginLeft: 8 }}>v{extVersion}</span>}</h3>
            <p className="dash-ext-desc">Scan, optimize, and generate cover letters directly from LinkedIn, Indeed, Dice & more.</p>
          </div>
        </div>
        <a href={`${API.replace('/api', '')}/api/extension/download${extVersion ? `?v=${extVersion}` : ""}`} className="dash-ext-dl-btn" data-testid="download-extension-dashboard" download>
          <Download size={14} /> Download{extVersion ? ` v${extVersion}` : ""}
        </a>
      </div>
    </div>
  );
}
